import { useEffect, useMemo, useState } from "react";
import { UseMutateFunction } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { SchoolHoursModel, SchoolYearType } from "model";
import { DisabledDateRanges } from "swing-components";
import { translate } from "utils/string-table";

import { TabItem, Tabs } from "~components/Molecules/Tabs/Tabs";
import { isSameDate } from "~components/RequestForm/hooks";

import { Footer, ProfileCard } from "../ProfileCard";
import { SchoolYearField } from "./SchoolYearField";
import { calcHours, getAcademicYearDateRange, getAcademicYears } from "./utils";

type PickerType = "start" | "end";
// Type representing an ISO 8601 date string in YYYY-MM-DD format (Example: "2023-08-15")
type ISODateString = string;
type SchoolYearsProps = {
  schoolYear?: SchoolYearType;
  setSchoolYear: UseMutateFunction<unknown, Error, SchoolYearType | undefined, unknown>;
  hours: SchoolHoursModel;

  // Props for tabbed view
  isTabbedView: boolean;
  tabs: ReadonlyArray<TabItem<string>>;
  tabsRangeData: Record<string, SchoolYearType>;
  selectedTab?: string;
  setSelectedTab?: (tab: string) => void;
};

export function SchoolYearsView(props: SchoolYearsProps) {
  const {
    schoolYear,
    setSchoolYear,
    hours,
    isTabbedView,
    tabs,
    tabsRangeData,
    selectedTab = tabs[0]?.value || "",
    setSelectedTab = () => {},
  } = props;

  const [isEditing, setIsEditing] = useState(false);
  const [_schoolYear, _setSchoolYear] = useState(schoolYear);
  const [_tabsRangeData, _setTabsRangeData] = useState(tabsRangeData);
  const [currentSelectedTab, setCurrentSelectedTab] = useState(selectedTab);

  useEffect(() => {
    setCurrentSelectedTab(selectedTab);
  }, [selectedTab]);

  useEffect(() => {
    _setSchoolYear(schoolYear);
  }, [schoolYear]);

  useEffect(() => {
    _setTabsRangeData(tabsRangeData);
  }, [tabsRangeData]);

  // Calculate academic year boundaries for the selected tab/school year
  const academicYearBoundaries = useMemo(() => {
    if (isTabbedView) {
      return getAcademicYearDateRange(currentSelectedTab, _tabsRangeData[currentSelectedTab]);
    } else {
      if (_schoolYear?.start && _schoolYear?.end) {
        return getAcademicYearDateRange("", _schoolYear);
      } else {
        // No school year loaded or no dates, use current academic year
        const currentAcademicYearString = getAcademicYears(DateTime.now())[0]?.value;
        if (currentAcademicYearString) {
          return getAcademicYearDateRange(currentAcademicYearString, undefined);
        }
        return null;
      }
    }
  }, [isTabbedView, currentSelectedTab, _tabsRangeData, _schoolYear]);

  const currentStartDate = isTabbedView
    ? _tabsRangeData[currentSelectedTab]?.start
    : _schoolYear?.start;
  const currentEndDate = isTabbedView ? _tabsRangeData[currentSelectedTab]?.end : _schoolYear?.end;

  const startDateInputDisabledDateRanges = getDisabledDateRangesForPicker(
    "start",
    currentEndDate,
    academicYearBoundaries,
  );

  const endDateInputDisabledDateRanges = getDisabledDateRangesForPicker(
    "end",
    currentStartDate,
    academicYearBoundaries,
  );

  const displayTabs = useMemo(() => {
    if (!isTabbedView) {
      return tabs;
    }

    return tabs.map((tab) => {
      const isEditingDifferentTab = isEditing && tab.value !== currentSelectedTab;
      return {
        ...tab,
        isDisabled: tab.isDisabled == true || isEditingDifferentTab,
      };
    });
  }, [isTabbedView, tabs, isEditing, currentSelectedTab]);

  function isFooterDisabled(): boolean {
    if (isTabbedView) {
      const currentTabStart = _tabsRangeData[currentSelectedTab]?.start;
      const currentTabEnd = _tabsRangeData[currentSelectedTab]?.end;
      if (!currentTabStart || !currentTabEnd) {
        return true;
      }

      const originalTabStart = tabsRangeData[currentSelectedTab]?.start;
      const originalTabEnd = tabsRangeData[currentSelectedTab]?.end;
      const isTabStartSame = isSameDate(
        DateTime.fromISO(originalTabStart || ""),
        DateTime.fromISO(currentTabStart || ""),
      );
      const isTabEndSame = isSameDate(
        DateTime.fromISO(originalTabEnd || ""),
        DateTime.fromISO(currentTabEnd || ""),
      );

      return isTabStartSame && isTabEndSame;
    }
    // For non-tabbed view
    if (!_schoolYear?.start || !_schoolYear?.end) {
      return true;
    }

    const isStartTheSame = isSameDate(
      DateTime.fromISO(schoolYear?.start || ""),
      DateTime.fromISO(_schoolYear.start || ""),
    );
    const isEndTheSame = isSameDate(
      DateTime.fromISO(schoolYear?.end || ""),
      DateTime.fromISO(_schoolYear.end || ""),
    );

    return isStartTheSame && isEndTheSame;
  }

  function handleSubmit() {
    setIsEditing(false);
    if (isTabbedView) {
      setSchoolYear(_tabsRangeData[currentSelectedTab]);
    } else {
      setSchoolYear(_schoolYear);
    }
  }

  function handleCancel() {
    setIsEditing(false);
    if (isTabbedView) {
      _setTabsRangeData((prev) => ({
        ...prev,
        [currentSelectedTab]: tabsRangeData[currentSelectedTab],
      }));
    } else {
      _setSchoolYear(schoolYear);
    }
  }

  function handleStartChange(value: string) {
    const newStartTime = calcHours({ hours, time: DateTime.fromISO(value), type: "start" });
    if (isTabbedView) {
      _setTabsRangeData((prev) => ({
        ...prev,
        [currentSelectedTab]: {
          ...(prev[currentSelectedTab] as SchoolYearType),
          start: newStartTime,
        },
      }));
    } else {
      _setSchoolYear((prevState) => ({
        ...(prevState as SchoolYearType),
        start: newStartTime,
      }));
    }
  }

  function handleEndChange(value: string) {
    const newEndTime = calcHours({ hours, time: DateTime.fromISO(value), type: "end" });
    if (isTabbedView) {
      _setTabsRangeData((prev) => ({
        ...prev,
        [currentSelectedTab]: {
          ...(prev[currentSelectedTab] as SchoolYearType),
          end: newEndTime,
        },
      }));
    } else {
      _setSchoolYear((prevState) => ({
        ...(prevState as SchoolYearType),
        end: newEndTime,
      }));
    }
  }

  return (
    <ProfileCard
      header={translate("SCHOOL_YEAR_HEADER")}
      isEditing={isEditing}
      onClick={() => setIsEditing(true)}
    >
      <p css={{ margin: 0, fontWeight: 400, fontSize: 16 }}>
        {translate("SCHOOL_YEAR_DESCRIPTION")}
      </p>

      {isTabbedView && (
        <div css={{ marginBottom: 16 }}>
          <Tabs
            tabs={displayTabs as Array<TabItem<string>>}
            initialTab={currentSelectedTab}
            onChange={(newTab) => {
              setCurrentSelectedTab(newTab);
              setSelectedTab(newTab);
            }}
          />
        </div>
      )}

      <ul css={{ padding: 0 }}>
        <SchoolYearField
          label={translate("SCHOOL_YEAR_START_LABEL")}
          value={isTabbedView ? _tabsRangeData[currentSelectedTab]?.start : _schoolYear?.start}
          isEditing={isEditing}
          disabledDateRanges={startDateInputDisabledDateRanges}
          onChange={handleStartChange}
        />
        <SchoolYearField
          label={translate("SCHOOL_YEAR_END_LABEL")}
          value={isTabbedView ? _tabsRangeData[currentSelectedTab]?.end : _schoolYear?.end}
          isEditing={isEditing}
          disabledDateRanges={endDateInputDisabledDateRanges}
          onChange={handleEndChange}
        />
      </ul>

      {isEditing && (
        <Footer disabled={isFooterDisabled()} onSubmit={handleSubmit} onCancel={handleCancel} />
      )}
    </ProfileCard>
  );
}

/**
 * Calculates disabled date ranges for a date picker based on the type of picker
 * and the constraining date.
 *
 * For start date pickers, dates after the end date will be disabled.
 * For end date pickers, dates before the start date will be disabled.
 * All dates outside the academic year boundaries will be disabled.
 *
 * @param pickerType - The type of picker("start" or "end")
 * @param constrainingDate - The constraining date value of the picker type in ISO format(YYYY-MM-DD)
 * @param academicYearBoundaries - The boundaries of the academic year
 *
 * @returns An array of disabled date ranges or undefined if no constraints
 */
function getDisabledDateRangesForPicker(
  pickerType: PickerType,
  constrainingDate?: ISODateString,
  academicYearBoundaries?: { start: ISODateString; end: ISODateString } | null,
): DisabledDateRanges | undefined {
  const calculatedRanges: Array<{ label?: string; start?: ISODateString; end?: ISODateString }> =
    [];

  if (academicYearBoundaries) {
    calculatedRanges.push(
      {
        end: DateTime.fromISO(academicYearBoundaries.start).toISODate(),
        label: !constrainingDate ? translate("SCHOOL_YEAR_INPUT_DISABLED_LABEL") : undefined,
      },
      {
        start: DateTime.fromISO(academicYearBoundaries.end).plus({ days: 1 }).toISODate(),
        label: !constrainingDate ? translate("SCHOOL_YEAR_INPUT_DISABLED_LABEL") : undefined,
      },
    );
  }

  if (constrainingDate) {
    const constrainingDateAsDateTime = DateTime.fromISO(constrainingDate).toISODate();
    if (pickerType === "start") {
      calculatedRanges.push({
        start: constrainingDateAsDateTime,
        label: translate("SCHOOL_YEAR_START_AFTER_END_DISABLED_LABEL", {
          endDate: DateTime.fromISO(constrainingDate),
        }),
      });
    } else {
      calculatedRanges.unshift({
        end: constrainingDateAsDateTime,
        label: translate("SCHOOL_YEAR_END_BEFORE_START_DISABLED_LABEL", {
          startDate: DateTime.fromISO(constrainingDate),
        }),
      });
    }
  }

  return calculatedRanges.length > 0 ? (calculatedRanges as DisabledDateRanges) : undefined;
}
